import React, { useState, useEffect, Suspense, lazy } from 'react';
import { Link } from 'react-router-dom';
import PropTypes from 'prop-types';
import { get, isEmpty } from 'lodash';
import Notification from 'common-ui-components/dist/components/Atoms/Notification';
import Anchor from 'common-ui-components/dist/components/Atoms/Anchor';
import { ReactSVG } from 'react-svg';
import Layout from '../Layout';
import QuickOrder from '../../molecules/QuickOrder';
import ShoppingCartItem from '../../organisms/ShoppingCartItem';
import ShoppingCartEmpty from '../ShoppingCartEmpty';
import ShoppingCartRemoveAllModal from './ShoppingCartRemoveAllModal';
import AlreadyHaveQuoteModal from './AlreadyHaveQuoteModal';
import ShareCartModal from '../../molecules/ShareCart/ShareCartModal';
import ShareCartSuccessModal from '../../molecules/ShareCart/ShareCartSuccessModal';
import ShoppingCartTotalItems from './ShoppingCartTotalItems';
import ShoppingCartUtilityButtons from './ShoppingCartUtilityButtons';
import OrderSummaryButtons from '../../molecules/OrderSummaryButtons';
import ShoppingCartNeedHelp from './ShoppingCartNeedHelp';
import ShoppingCartPersistenceMessage from './ShoppingCartPersistenceMessage';
import DisabledContainer from '../../molecules/DisabledContainer';
import './ShoppingCart.scss';
import OrderSummary from '../../organisms/OrderSummary';
import Notes from '../../molecules/Notes';
import {
  getLabelWithComponent,
  downloadXLSX,
  isUserLoggedIn,
  removeLastPartFromUrl,
  scrollToTop,
  getSoldToId,
  isEProcurementUserOnECommerce,
  isUnavailableItem,
  getBoolValue,
  getDefaultPathVar,
  getStore,
} from '../../../utils/utils';
import OrderSummaryMiniCart from '../../molecules/OrderSummaryMiniCart';
import AccountName from '../../molecules/AccountName/AccountName';
import {
  USER_STATE_LOGGED_IN,
  FULL_ENABLED,
  PARTIAL_ENABLED,
  QUICK_ORDER_URL,
  CHECKOUT_URL,
  CREATE_QUOTE_URL,
} from '../../../global/constants';
import {
  PRODUCT_REFERENCES,
  PRODUCT_REFERENCES_WITH_REPLACEMENT,
  FULL,
} from '../../../containers/templates/ShoppingCart/ShoppingCart.constants';
import {
  isErrorCodeExistInLabel,
  getTernaryCondition,
  getSoldToAddress,
} from '../../../utils/utilFunctions';
import BogoCreateQuoteModal from '../../molecules/Promotional/BogoCreateQuoteModal';
import ProductRecommendations from '../../molecules/ProductRecommendations/ProductRecommendations';

const ShoppingCartBlockedAccountModal = lazy(() => import('./ShoppingCartBlockedAccountModal'));
const SwitchAccountModal = lazy(() => import('../../molecules/SwitchAccountModal'));

const getIsFreeItem = (isFreeGift, isFreeProduct) => isFreeGift || isFreeProduct;

const isCheckoutButtonDisabled = (cartItems, productErrors = [], globalErr = []) => {
  let status = productErrors.length > 0 || globalErr.length > 0;
  if (cartItems.length > 0) {
    cartItems.map(item => {
      const { product = {}, isFreeGift = false, isFreeProduct = false } = item;
      const { salesStatus = undefined, purchasable = false, addToCartDisabled = false } = product;
      const isFreeItem = getIsFreeItem(isFreeGift, isFreeProduct);
      if (!isEmpty(salesStatus) && PRODUCT_REFERENCES[salesStatus]) {
        status = true;
      }
      if (!isEmpty(salesStatus) && PRODUCT_REFERENCES_WITH_REPLACEMENT[salesStatus]) {
        status = true;
      }
      if ((!purchasable || addToCartDisabled) && !isFreeItem) {
        status = true;
      }
      return status;
    });
  }
  return status;
};

const totalCharges = charges => {
  const value = get(charges, 'value', 0);
  const formattedValue = get(charges, 'formattedValue');
  return charges && parseFloat(value) > 0 ? formattedValue : undefined;
};

// eslint-disable-next-line max-statements, complexity
const ShoppingCart = props => {
  const [inputValue, setInputValue] = useState();
  const cartItems = get(props, 'cartData.entries', []);
  const productError = get(props, 'cartData.error.productErrors', []);
  const globalError = get(props, 'cartData.error.globalErrors', []);
  const totalItems = get(props, 'cartData.totalItems', 0);
  const totalPrice = get(props, 'cartData.totalPrice.formattedValue', undefined);
  const totalPriceWithTax = get(props, 'cartData.totalPriceWithTax.formattedValue', undefined);
  const taxValue = get(props, 'cartData.totalTax', undefined);
  const totalTax = totalCharges(taxValue);
  const totalDiscounts =
    get(props, 'cartData.totalDiscounts.value') > 0
      ? `-${get(props, 'cartData.totalDiscounts.formattedValue')}`
      : undefined;
  const subTotal = get(props, 'cartData.subTotal.formattedValue', undefined);
  const totalBasePriceWithTax = get(
    props,
    'cartData.totalBasePriceWithTax.formattedValue',
    undefined
  );
  const store = getStore();
  const SP_PUBLIC_KEY = get(
    store,
    ['global', 'globalData', 'countryConfig', 'SP_PUBLIC_KEY'],
    sessionStorage.getItem('SP_PUBLIC_KEY') !== 'undefined'
      ? sessionStorage.getItem('SP_PUBLIC_KEY')
      : undefined
  );
  const isCartRemoved = get(props, 'shoppingCartData.isCartRemoved', false);
  const isCartItemRemoved = get(props, 'shoppingCartData.isCartItemRemoved', false);
  const removedProductName = get(props, 'shoppingCartData.removedProductName', '');
  const removedItemUrl = get(props, 'shoppingCartData.removedItemUrl', '');
  const isCartItemAdded = get(props, 'shoppingCartData.isCartItemAdded', false);
  const countryData = get(props, 'countryData', {});
  const soldToData = get(props, ['soldTo'], '');
  const createSetupIntentId = get(props, ['createSetupIntentId'], '');
  const dummySoldtoData = get(props, ['dummySoldTo'], '');
  const userState = get(props, 'userState', 'ANONYMOUS');
  const labels = get(props, 'labels', {});
  const config = get(props, 'config', {});
  const approvalStatus = get(props, ['approvalStatus'], '');
  const countryEnabledStatus = get(
    props,
    ['countryStatus', 'Page Content', 'countryCommerceConfig'],
    FULL_ENABLED
  );
  const promoCodeDetails = get(props, 'promoCodeDetails', {});
  const addToCart = get(props, 'addToCart');
  const productRecommendationAddToCart = get(props, 'productRecommendationAddToCart');
  const productRecommendationTitleClick = get(props, 'productRecommendationTitleClick');
  const removeCartItem = get(props, 'removeCartItem');
  const removeCart = get(props, 'removeCart');
  const shoppingCartError = get(props, 'shoppingCartData.error', '');
  const contactUsAnchor = (
    <Anchor
      className="row cart-inline-error-contact-us-link"
      elementLocator="add-to-cart-invalid-sku-error-contact-us-link"
      to={config.contactUsLink}
    >
      {labels.contactWaters}
    </Anchor>
  );
  const inlineError = isErrorCodeExistInLabel(labels, shoppingCartError)
    ? getLabelWithComponent(labels[shoppingCartError.code], [inputValue, contactUsAnchor])
    : '';

  const coupons = get(props, 'cartData.coupons', []);
  const couponsError = get(props, 'cartData.couponErrors');
  const downloadCSVSuccess = get(props, 'downloadCSVSuccess');
  const shippingAndHandling = get(props, 'cartData.totalShippingAndHandling', undefined);
  const userDetails = get(props, 'userDetails', {});
  const { localeLanguage } = getDefaultPathVar();
  const userEmail = get(userDetails, 'email', '');
  const totalShippingAndHandling = totalCharges(shippingAndHandling);
  const [isModalRemoveAllOpen, setIsModalRemoveAllOpen] = useState(false);
  const [isModalBlockedAccountOpen, setIsModalBlockedAccountOpen] = useState(false);
  const [isResetCart, setResetCart] = useState(false);
  const [showAlreadyQuote, setShowAlreadyQuote] = useState(false);
  const [showShareCartModal, setShowShareCartModal] = useState(false);
  const [showShareCartSuccessModal, setShowShareCartSuccessModal] = useState(false);
  const [bogoQuoteModalStatus, setBogoQuoteModalStatus] = useState(false);
  const shareCartData = get(props, 'shareCartData', {});
  const shippingInfo = get(props, 'shippingInfo', []);
  const orderSummaryData = {
    totalItems,
    totalPrice,
    totalDiscounts,
    totalPriceWithTax,
    totalTax,
    subTotal,
    coupons,
    couponsError,
    promoCodeDetails,
    totalShippingAndHandling,
    totalBasePriceWithTax,
  };
  const isAPILoading = get(props, 'isAPILoading', false);
  const pathname = get(props, 'location.pathname', '');
  const loginUserStatus = isUserLoggedIn();
  const showFreightInfo = true;
  const {
    signInUrl,
    multipleIconUrl,
    collapseIcon,
    openIcon,
    userIcon,
    continueShoppingUrl,
    contactUsLink,
    freeItemIconUrl,
    removeAllItemsIconUrl,
    successIconUrl,
    unavailableIconUrl,
    closeIcon,
    cartIcon,
    isQuoteToOrderDisabled,
    shareCartIcon,
  } = config;
  const { shareCart } = labels;
  const needHelpClass =
    userState === USER_STATE_LOGGED_IN
      ? 'cart-need-help-adjustment'
      : 'cart-need-help-anonymous-adjustment';
  let quickOrderElem = null;
  const locator = 'shopping-cart';
  const {
    onRequestAQuote,
    addPromoCode,
    getpromoCodeDetails,
    showOrderNotAvailableView,
    showBlockedAccountModal,
    requestQuoteClick,
    resetCouponError,
    loadUserEmail,
    shareCartSubmit,
    shareCartAnalytics,
    shareCartClear,
    userAuthentication,
    shortTermTokeExpiredData,
    countryConfig,
    updateCartQuantity,
    setSwitchAccount,
  } = props;

  const checkoutButtonDisabled = isCheckoutButtonDisabled(cartItems, productError, globalError);
  const isCommerceCheckoutButtonHide = get(countryConfig, ['checkoutDisabled'], false);
  const isQuoteDisabled = get(countryConfig, ['quoteDisabled'], false);
  const showFaPiaoHistory = get(countryConfig, ['showFaPiaoHistory'], false);
  const includeTaxWithPrice = get(countryConfig, ['includeTaxWithPrice'], false);
  const products = [];
  const quickOrderUrl = `${removeLastPartFromUrl(pathname)}/${QUICK_ORDER_URL}`;
  const checkoutUrl = `${removeLastPartFromUrl(pathname)}/${CHECKOUT_URL}`;
  const createQuoteUrl = `${removeLastPartFromUrl(pathname)}/${CREATE_QUOTE_URL}`;
  const isQuoteToOrderDisabledStatus = getBoolValue(isQuoteToOrderDisabled);

  const requestQuoteButtonClick = () => {
    const {
      dummySoldto,
      email,
      firstName,
      lastName,
      namePrefix,
      phone,
      sapWebUserId,
      userId,
    } = userDetails;
    const partners = [];
    const soldToId = getSoldToId();
    if (!isEmpty(soldToId)) {
      partners.push({ partnerNumber: soldToId });
    }

    const obj = {
      salesOrg: countryData.salesOrg,
      partners,
      products,
      isNewUser: !isEmpty(dummySoldto),
      email,
      firstName,
      lastName,
      namePrefix,
      phone,
      sapWebUserId,
      userId,
    };
    return onRequestAQuote(obj);
  };
  const downloadXLSXFile = () => {
    downloadXLSX(cartItems);
    downloadCSVSuccess();
  };

  useEffect(() => {
    if (isCartItemAdded && quickOrderElem) {
      quickOrderElem.reset();
      setInputValue();
    }
  });

  React.useEffect(() => {
    if (!isEmpty(shortTermTokeExpiredData)) {
      setShowAlreadyQuote(false);
    }
  }, [shortTermTokeExpiredData]);
  const checkBlockAccount = () =>
    countryEnabledStatus === FULL_ENABLED &&
    !isEmpty(userDetails) &&
    soldToData.length <= 0 &&
    isEmpty(dummySoldtoData);

  useEffect(() => {
    if (isModalBlockedAccountOpen === true) {
      showBlockedAccountModal();
    }
  }, [isModalBlockedAccountOpen]);

  const [isModalBlockedFullEnabled, setIsModalBlockedFullEnabled] = useState(false);
  const showBlockedAccountBox = checkBlockAccount();

  const alreadyHaveAQuote = () => {
    setShowAlreadyQuote(true);
  };

  const validateQuoteId = e => {
    userAuthentication({
      onValidateQuote: { quoteCode: e, field: FULL, disableRestrictions: true },
    });
  };
  useEffect(() => {
    setIsModalBlockedFullEnabled(showBlockedAccountBox);
  }, [showBlockedAccountBox]);

  useEffect(() => {
    if (isResetCart === true) {
      removeCart();
      scrollToTop();
      setResetCart(false);
    }
  }, [isResetCart]);

  useEffect(() => {
    resetCouponError();
  }, []);

  useEffect(() => {
    const success = get(shareCartData, 'success', '');
    const errors = get(shareCartData, ['errors', '0'], {});
    if (!isEmpty(success)) {
      setShowShareCartModal(false);
      setShowShareCartSuccessModal(true);
    }
    if (!isEmpty(errors)) {
      setShowShareCartModal(false);
      setShowShareCartSuccessModal(false);
    }
  }, [shareCartData]);

  const openBlockedAccountModal = e => {
    e.preventDefault();
    setIsModalBlockedAccountOpen(true);
  };
  const [showSwitchAccount, setShowSwitchAccount] = useState(false);
  const openSwitchAccountModal = e => {
    e.preventDefault();
    setShowSwitchAccount(true);
  };

  const renderRemovedItem = (productName, url) => <Anchor to={url}>{productName}</Anchor>;
  const renderEmptyCart = () => (
    <div className="col-xs-12" data-locator="shopping-cart-empty-section">
      <ShoppingCartTotalItems
        cartItemsNumber={totalItems}
        headingLabel={labels.shoppingCart}
        itemsLabel={labels.items}
        itemLabel={labels.item}
      />
      <div className="row col-sm-12 middle-xs hidden-xs hidden-sm">
        <QuickOrder
          inputPlaceholder={labels.codePlaceholder}
          qtyPlaceholder={labels.qtyPlaceholder}
          submitButtonLabel={labels.addToCart}
          pageId="cart"
          elementLocator="cart"
          {...{
            onSubmit: (skuValue, qty) => {
              setInputValue(skuValue);
              addToCart(skuValue, qty);
            },
          }}
        />
        <Link
          to={quickOrderUrl}
          className="row quick-order-multiple-item"
          data-locator="link-quick-order-add-multiple-item"
          aria-label={labels.addMultipleItems}
        >
          {multipleIconUrl && <ReactSVG src={multipleIconUrl} />}
          {labels.addMultipleItems}
        </Link>
      </div>
      <div className="row col-sm-12 middle-xs hidden-md hidden-lg hidden-xl">
        <Link
          to={quickOrderUrl}
          className="row quick-order-add-item-to-cart"
          data-locator="link-quick-order-add-item-to-cart"
        >
          {config.addItemsToCart && <ReactSVG src={config.addItemsToCart} />}
          {labels.addItemsToCart}
        </Link>
      </div>
      {(isCartRemoved || isCartItemRemoved) && (
        <div className="cart-inline-notification">
          <Notification
            variation="inline"
            type="success"
            description={labels.itemsRemovedConfirmation}
            icon={successIconUrl}
            className="col-xs-12"
            elementLocator="notification-inline-success"
          />
        </div>
      )}
      {inlineError && !!inputValue && (
        <div className="cart-inline-notification">
          <Notification
            variation="inline"
            type="error"
            description={inlineError}
            icon={removeAllItemsIconUrl}
            className="col-xs-12"
            elementLocator="notification-inline-error"
          />
        </div>
      )}
      <ShoppingCartEmpty
        {...{
          userState,
          labels,
          signInUrl,
          continueShoppingUrl,
          userIcon,
          alreadyHaveAQuote,
          openBlockedAccountModal,
          isAccountBlocked: approvalStatus === 'R',
          checkoutButtonDisabled: isCommerceCheckoutButtonHide,
          isQuoteDisabled,
          isQuoteToOrderDisabled: isQuoteToOrderDisabledStatus,
        }}
      />
    </div>
  );
  const renderDisabledCart = () => (
    <div className="col-xs-12" data-locator="shopping-cart-disabled-section">
      <h1>{labels.shoppingCart}</h1>
      <DisabledContainer
        {...{
          userState,
          labels,
          signInUrl,
          contactUsLink,
          locator,
          onMount: showOrderNotAvailableView,
          countryEnabledStatus,
        }}
      />
    </div>
  );

  const getOrderSummaryButtons = () => (
    <>
      <OrderSummaryButtons
        spPublicKey={SP_PUBLIC_KEY}
        labels={labels}
        createSetupIntentId={createSetupIntentId}
        userState={userState}
        signInUrl={signInUrl}
        checkoutButtonDisabled={checkoutButtonDisabled}
        continueShoppingUrl={continueShoppingUrl}
        isAccountBlocked={approvalStatus === 'R'}
        checkoutUrl={checkoutUrl}
        createQuoteUrl={createQuoteUrl}
        documentIconUrl={config.documentIconUrl}
        isCommerceCheckoutButtonHide={isCommerceCheckoutButtonHide}
        isQuoteDisabled={isQuoteDisabled}
        requestQuoteClick={requestQuoteClick}
        isQuoteToOrderDisabled={isQuoteToOrderDisabledStatus}
        userAuthentication={userAuthentication}
        setBogoQuoteModalStatus={setBogoQuoteModalStatus}
        {...{
          onRequestAQuote: requestQuoteButtonClick,
          openBlockedAccountModal,
          alreadyHaveAQuote,
        }}
      />
      {userState === USER_STATE_LOGGED_IN && totalDiscounts && (
        <Notes text={labels.orderSummaryNote} />
      )}
    </>
  );
  const renderNotifications = () => (
    <>
      {isCartItemRemoved && (
        <div className="cart-inline-notification">
          <Notification
            variation="inline"
            type="success"
            title={renderRemovedItem(removedProductName, removedItemUrl)}
            description={labels.removeCartItemSuccessMessage}
            icon="/static/images/success.svg"
            className="col-xs-12 cart-item-removed"
            elementLocator="notification-removed-all-item"
          />
        </div>
      )}
      {inlineError && !!inputValue && (
        <div className="cart-inline-notification">
          <Notification
            variation="inline"
            type="error"
            description={inlineError}
            icon="/static/images/error.svg"
            className="col-xs-12"
            elementLocator="notification-inline-error"
          />
        </div>
      )}
    </>
  );

  const renderOrderSummaryMiniCart = () => (
    <OrderSummaryMiniCart
      className="hidden-md hidden-lg hidden-xl"
      labels={labels}
      data={orderSummaryData}
      userState={userState}
      signInUrl={signInUrl}
      checkoutButtonDisabled={checkoutButtonDisabled}
      continueShoppingUrl={continueShoppingUrl}
      {...{ openBlockedAccountModal }}
      isAccountBlocked={approvalStatus === 'R'}
      checkoutUrl={checkoutUrl}
      documentIconUrl={config.documentIconUrl}
      isCommerceCheckoutButtonHide={isCommerceCheckoutButtonHide}
      includeTaxWithPrice={includeTaxWithPrice}
      userAuthentication={userAuthentication}
    />
  );

  const renderNeedHelpSection = () => (
    <ShoppingCartNeedHelp
      className={`col-xs-12 col-md-5 ${cartItems.length === 1 ? needHelpClass : ''}`}
      needHelp={labels.needHelp}
      orderSupport={labels.orderSupport}
      contactWaters={labels.contactWaters}
      freightInfo={labels.freightInfo}
      freightPolicy={labels.freightPolicy}
      needHelpChatIconUrl={config.needHelpChatIconUrl}
      needHelpFreightIconUrl={config.needHelpFreightIconUrl}
      contactUsLink={config.contactUsLink}
      freightPolicyLink={config.freightPolicyLink}
      showFreightInfo={showFreightInfo}
    />
  );

  const renderBlockFullEnabledModal = () =>
    isModalBlockedFullEnabled ? (
      <Suspense fallback={<div />}>
        <ShoppingCartBlockedAccountModal
          title={labels.blockedAccountTitle}
          description={labels.blockedAccountDescription}
          isModalBlockedAccountOpen={isModalBlockedFullEnabled}
          setIsModalBlockedAccountOpen={setIsModalBlockedFullEnabled}
          unavailableIconUrl={unavailableIconUrl}
          contactUsLink={config.contactUsLink}
          contactWaters={labels.contactWaters}
        />
      </Suspense>
    ) : null;

  const checkCountryEnabledStatus = () =>
    countryEnabledStatus === FULL_ENABLED ||
    (countryEnabledStatus === PARTIAL_ENABLED && soldToData.length > 0);

  const handleShareCartButtonClick = () => {
    if (loginUserStatus && isEmpty(userEmail)) {
      loadUserEmail();
    }
    shareCartAnalytics();
    setShowShareCartModal(true);
    setShowShareCartSuccessModal(false);
  };

  const onShareCartSubmit = data => {
    shareCartSubmit(data);
  };

  const setShareCartModalClose = status => {
    setShowShareCartModal(status);
    setShowShareCartSuccessModal(status);
    shareCartClear({});
  };

  const getShareCartSuccessModal = () =>
    showShareCartSuccessModal && (
      <ShareCartSuccessModal
        showShareCartSuccessModal={showShareCartSuccessModal}
        {...{ setShareCartModalClose }}
        config={config}
        labels={labels}
      />
    );

  const getBogoQuoteModal = () => (
    <BogoCreateQuoteModal
      labels={labels}
      unavailableIconUrl={unavailableIconUrl}
      bogoQuoteModalStatus={bogoQuoteModalStatus}
      setBogoQuoteModalStatus={setBogoQuoteModalStatus}
    />
  );
  return (
    <Layout
      title="shopping cart"
      className="row"
      id="content-wrapper"
      data-locator="shopping-cart-layout-section"
      isQuoteDisabled={isQuoteDisabled}
      showFaPiaoHistory={showFaPiaoHistory}
    >
      <div className="row">
        {checkCountryEnabledStatus() && !isEProcurementUserOnECommerce() ? (
          <>
            <ShoppingCartRemoveAllModal
              removeItems={labels.removeItems}
              itemsNotSaved={labels.itemsNotSaved}
              removeAllItems={labels.removeAllItems}
              cancel={labels.cancel}
              isModalRemoveAllOpen={Boolean(isModalRemoveAllOpen)}
              setIsModalRemoveAllOpen={setIsModalRemoveAllOpen}
              removeCart={setResetCart}
              unavailableIconUrl={unavailableIconUrl}
            />

            <AlreadyHaveQuoteModal
              showAlreadyQuote={Boolean(showAlreadyQuote)}
              setShowAlreadyQuote={setShowAlreadyQuote}
              cartIconUrl={cartIcon}
              labels={labels}
              {...{ onSubmit: validateQuoteId }}
              errorObj={shoppingCartError}
              quoteErrorIconUrl={unavailableIconUrl}
            />

            <ShareCartModal
              showShareCartModal={showShareCartModal}
              setShowShareCartModal={setShowShareCartModal}
              config={config}
              labels={labels}
              isoCode={localeLanguage}
              userEmail={userEmail}
              cartItems={cartItems}
              includeTaxWithPrice={includeTaxWithPrice}
              isUserLoggedIn={loginUserStatus}
              {...{
                downloadXLSXFile,
                onShareCartSubmit,
              }}
            />

            {getShareCartSuccessModal()}

            {totalItems > 0 && (
              <>
                <div className="col-md-5 col-xs-12 col-lg-8">
                  <ShoppingCartTotalItems
                    cartItemsNumber={totalItems}
                    headingLabel={labels.shoppingCart}
                    itemsLabel={labels.items}
                    itemLabel={labels.item}
                  />
                  {renderOrderSummaryMiniCart()}
                  <div className="row col-sm-12 middle-xs hidden-xs hidden-sm">
                    <QuickOrder
                      ref={elem => {
                        quickOrderElem = elem;
                      }}
                      pageId="cart"
                      elementLocator="cart"
                      inputPlaceholder={labels.codePlaceholder}
                      qtyPlaceholder={labels.qtyPlaceholder}
                      submitButtonLabel={labels.addToCart}
                      {...{
                        onSubmit: (skuValue, qty) => {
                          setInputValue(skuValue);
                          addToCart(skuValue, qty);
                        },
                      }}
                    />
                    <Link
                      to={quickOrderUrl}
                      className="row quick-order-multiple-item"
                      data-locator="link-quick-order-add-multiple-item"
                      aria-label={labels.addMultipleItems}
                    >
                      <ReactSVG src={multipleIconUrl} />
                      {labels.addMultipleItems}
                    </Link>
                  </div>
                  <div className="row col-sm-12 middle-xs hidden-md hidden-lg hidden-xl">
                    <Link
                      to={quickOrderUrl}
                      className="row quick-order-add-item-to-cart"
                      data-locator="link-quick-order-add-item-to-cart"
                    >
                      <ReactSVG src={config.addItemsToCart} />
                      {labels.addItemsToCart}
                    </Link>
                  </div>
                  {soldToData.length > 1 && (
                    <div
                      className="col-md-12 col-xs-12 col-sm-12 switch-account-container"
                      data-locator="switch-account-container"
                    >
                      <span
                        className="col-md-2 col-xs-5 account-name-label"
                        data-locator="account-name-label"
                      >
                        {`${labels.accountName}:`}
                      </span>
                      {getSoldToAddress(userDetails, soldToData)}
                      <AccountName
                        {...props}
                        soldTo={soldToData}
                        openSwitchAccountModal={openSwitchAccountModal}
                      />
                    </div>
                  )}
                  {renderNotifications()}
                  <div className="cart-items-parent col-xs-12">
                    {cartItems.map(item => {
                      const { product = {}, quantity } = item;
                      const { salesStatus = undefined } = product;
                      let isObsoleteDiscontinued = false;
                      let isObsoleteDiscontinuedWithReplacement = false;
                      const isSKUUnbuyable = productError.find(p => p.id === product.code);
                      if (!isEmpty(salesStatus) && PRODUCT_REFERENCES[salesStatus]) {
                        isObsoleteDiscontinued = true;
                      }
                      if (
                        !isEmpty(salesStatus) &&
                        PRODUCT_REFERENCES_WITH_REPLACEMENT[salesStatus]
                      ) {
                        isObsoleteDiscontinuedWithReplacement = true;
                      }
                      products.push({ number: product.code, quantity });
                      return (
                        <ShoppingCartItem
                          productItem={item}
                          labels={labels}
                          config={config}
                          key={item.entryNumber}
                          updateCartQuantity={updateCartQuantity}
                          removeCartItem={removeCartItem}
                          isUserLoggedIn={loginUserStatus}
                          disabled={isAPILoading}
                          isObsoleteDiscontinued={isObsoleteDiscontinued}
                          isObsoleteDiscontinuedWithReplacement={
                            isObsoleteDiscontinuedWithReplacement
                          }
                          isSKUUnbuyable={isSKUUnbuyable}
                          isUnavailableItem={isUnavailableItem(product.code, globalError)}
                          includeTaxWithPrice={includeTaxWithPrice}
                        />
                      );
                    })}
                    <ShoppingCartUtilityButtons
                      removeAllItems={labels.removeAllItems}
                      downloadItems={labels.downloadItems}
                      removeAllItemsIconUrl={config.removeAllItemsIconUrl}
                      downloadItemsIconUrl={config.downloadItemsIconUrl}
                      setIsModalRemoveAllOpen={setIsModalRemoveAllOpen}
                      {...{ downloadCart: downloadXLSXFile }}
                    />
                  </div>
                  {!loginUserStatus && (
                    <ShoppingCartPersistenceMessage
                      className="col-xs-12 cart-persistence-message"
                      labels={labels}
                      config={config}
                    />
                  )}
                </div>
                <div className="col-lg-1 hidden-md" />
                <OrderSummary
                  className="col-md-3 col-xs-12 col-lg-3"
                  labels={labels}
                  collapseIcon={collapseIcon}
                  openIcon={openIcon}
                  data={orderSummaryData}
                  userState={userState}
                  userIcon={userIcon}
                  signInUrl={signInUrl}
                  addPromoCode={addPromoCode}
                  getpromoCodeDetails={getpromoCodeDetails}
                  promotionIconUrl={freeItemIconUrl}
                  errorIcon={removeAllItemsIconUrl}
                  closeIcon={closeIcon}
                  isCart
                  includeTaxWithPrice={includeTaxWithPrice}
                  shareCartIcon={shareCartIcon}
                  isShareCartEnabled={!isEmpty(shareCart)}
                  {...{ handleShareCartButtonClick }}
                  shippingInfo={shippingInfo}
                  isPromotionDisabled={countryConfig.promotionDisabled}
                >
                  {getOrderSummaryButtons()}
                </OrderSummary>

                {isModalBlockedAccountOpen && (
                  <Suspense fallback={<div />}>
                    <ShoppingCartBlockedAccountModal
                      title={labels.blockedAccountTitle}
                      description={labels.blockedAccountDescription}
                      isModalBlockedAccountOpen={isModalBlockedAccountOpen}
                      setIsModalBlockedAccountOpen={setIsModalBlockedAccountOpen}
                      unavailableIconUrl={unavailableIconUrl}
                      contactUsLink={config.contactUsLink}
                      contactWaters={labels.contactWaters}
                    />
                  </Suspense>
                )}
              </>
            )}
            {totalItems <= 0 && renderEmptyCart()}
            {renderBlockFullEnabledModal()}
            {renderNeedHelpSection()}
            {getBogoQuoteModal()}
          </>
        ) : (
          renderDisabledCart()
        )}
      </div>
      <ProductRecommendations
        productRecommendationData={get(props, 'productRecommendationData', [])}
        customClassName={getTernaryCondition(
          totalItems > 0,
          'col-md-12 col-xs-12 col-lg-12',
          'col-xs-12'
        )}
        labels={labels}
        config={config}
        productRecommendationAddToCart={productRecommendationAddToCart}
        shoppingCartError={shoppingCartError}
        productRecommendationTitleClick={productRecommendationTitleClick}
      />
      {showSwitchAccount && (
        <Suspense fallback={<div />}>
          <SwitchAccountModal
            labels={labels}
            config={config}
            soldToList={soldToData}
            setSwitchAccount={setSwitchAccount}
            showSwitchAccount={showSwitchAccount}
            setShowSwitchAccount={setShowSwitchAccount}
            userIcon={config.userIcon}
          />
        </Suspense>
      )}
    </Layout>
  );
};
ShoppingCart.propTypes = {
  /* eslint react/forbid-prop-types: 0 */
  updateCartQuantity: PropTypes.func,
  onRequestAQuote: PropTypes.func,
  addPromoCode: PropTypes.func,
  getpromoCodeDetails: PropTypes.func,
  showOrderNotAvailableView: PropTypes.func,
  showBlockedAccountModal: PropTypes.func,
  requestQuoteClick: PropTypes.func,
  resetCouponError: PropTypes.func,
  loadUserEmail: PropTypes.func,
  shareCartSubmit: PropTypes.func,
  shareCartAnalytics: PropTypes.func,
  shareCartClear: PropTypes.func,
  userAuthentication: PropTypes.func,
  shortTermTokeExpiredData: PropTypes.objectOf(PropTypes.string),
  countryConfig: PropTypes.objectOf(PropTypes.string),
  setSwitchAccount: PropTypes.func,
  productRecommendationData: PropTypes.arrayOf(
    PropTypes.shape({
      code: PropTypes.string,
      addToCartDisabled: PropTypes.bool,
      purchasable: PropTypes.bool,
      isLoading: PropTypes.bool,
      custPrice: PropTypes.string,
      custValue: PropTypes.string,
      listPrice: PropTypes.string,
      listValue: PropTypes.string,
      currencyCode: PropTypes.string,
    })
  ),
};

ShoppingCart.defaultProps = {
  shortTermTokeExpiredData: {},
  countryConfig: {},
  productRecommendationData: [],
  updateCartQuantity: () => {
    /* default updateCartQuantity handler */
  },
  onRequestAQuote: () => {
    /* default onRequestAQuote handler */
  },
  addPromoCode: () => {
    /* default addPromoCode handler */
  },
  getpromoCodeDetails: () => {
    /* default getpromoCodeDetails handler */
  },
  showOrderNotAvailableView: () => {
    /* default showOrderNotAvailableView handler */
  },
  showBlockedAccountModal: () => {
    /* default showBlockedAccountModal handler */
  },
  requestQuoteClick: () => {
    /* default requestQuoteClick handler */
  },
  resetCouponError: () => {
    /* default resetCouponError handler */
  },
  loadUserEmail: () => {
    /* default loadUserEmail handler */
  },
  shareCartSubmit: () => {
    /* default shareCartSubmit handler */
  },
  shareCartAnalytics: () => {
    /* default shareCartAnalytics handler */
  },
  shareCartClear: () => {
    /* default shareCartClear handler */
  },
  userAuthentication: () => {
    /* default userAuthentication handler */
  },
  setSwitchAccount: () => {
    /* default setSwitchAccount handler */
  },
};

export default ShoppingCart;
